import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  List, 
  Box, 
  Drawer, 
  IconButton, 
  Divider, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText,
  Typography,
  AppBar,
  Toolbar,
  Container,
  Tooltip
} from '@mui/material';
import {
  Home,
  CalendarToday,
  Receipt,
  Spa,
  CalendarMonth,
  Inventory,
  People,
  AttachMoney,
  Close,
  AdminPanelSettings
} from '@mui/icons-material';
import InterfaceMenu from './InterfaceMenu';
import MenuIcon from '@mui/icons-material/Menu';
import Logo from '../../assets/pictures/logo-sf.png';
import NotificationRing from './NotificationRing';
import '../../Styles/Headers/HeaderAuth.css';

function HeaderAuth({ token, employee }) {
  const [open, setOpen] = useState(false);
  const [storedToken, setToken] = useState(token);

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('storedToken');
    if (token) {
      setToken(token);
    }
  }, []);

  const menuOptions = [
    { label: 'Inicio', path: '/home', icon: <Home /> },
    { label: 'Reservaciones', path: `/events`, icon: <CalendarToday /> },
    { label: 'Facturación', path: `/billing`, icon: <Receipt /> },
    { label: 'Servicios', path: `/services`, icon: <Spa /> },
    { label: 'Agenda', path: `/calendars`, icon: <CalendarMonth /> },
    { label: 'Inventario', path: `/inventory`, icon: <Inventory /> },
    { label: 'Clientes', path: `/clients`, icon: <People /> },
    { label: 'Finanzas', path: `/finances`, icon: <AttachMoney /> },
    { label: 'Administración', path: `/administration`, icon: <AdminPanelSettings /> }
  ];

  const handleClick = (event, path) => {
    event.preventDefault();
    setOpen(false);
    navigate(path);
  };

  const toggleDrawer = (isOpen) => () => setOpen(isOpen);

  const DrawerContent = (
    <Box 
      sx={{ 
        width: { xs: 280, sm: 320 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }} 
      role="presentation"
    >
      {/* Header del Drawer */}
      <Box 
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: { xs: 2, sm: 3 },
          background: 'linear-gradient(135deg, #f759ab 0%, #e91e63 100%)',
          color: 'white',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <img 
            src={Logo} 
            alt="Logo" 
            style={{ 
              height: '35px',
              filter: 'brightness(0) invert(1)'
            }} 
          />
        </Box>
        <Tooltip title="Cerrar menú">
          <IconButton 
            onClick={toggleDrawer(false)} 
            sx={{ 
              color: 'inherit',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <Close />
          </IconButton>
        </Tooltip>
      </Box>
      
      {/* Menú de navegación */}
      <List sx={{ py: 1, flexGrow: 1 }}>
        {menuOptions.map((option, index) => (
          <ListItem key={index} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={(e) => handleClick(e, option.path)}
              sx={{
                mx: 1,
                borderRadius: 2,
                py: { xs: 1.5, sm: 2 },
                px: 2,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  bgcolor: 'rgba(247, 89, 171, 0.1)',
                  transform: 'translateX(4px)',
                  '& .MuiListItemIcon-root': { 
                    color: '#f759ab',
                    transform: 'scale(1.1)'
                  },
                  '& .MuiTypography-root': {
                    color: '#f759ab',
                    fontWeight: 600
                  }
                }
              }}
            >
              <ListItemIcon 
                sx={{ 
                  color: '#666', 
                  minWidth: '40px',
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                {option.icon}
              </ListItemIcon>
              <ListItemText 
                primary={
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      transition: 'all 0.2s ease-in-out'
                    }}
                  >
                    {option.label}
                  </Typography>
                } 
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      
      {/* Footer del drawer - ahora al fondo */}
      <Box sx={{ mt: 'auto' }}>
        <Divider sx={{ mx: 2, my: 2 }} />
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            D'Princess Foto Films © 2025
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="sticky" 
        elevation={2}
        sx={{
          background: 'linear-gradient(135deg, #f759ab 0%, #e91e63 100%)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}
      >
        <Container maxWidth="xl">
          <Toolbar 
            sx={{ 
              minHeight: { xs: '56px', sm: '64px' },
              px: { xs: 1, sm: 2 },
              py: 1
            }}
          >
            {/* Botón del menú hamburguesa */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={toggleDrawer(true)}
                sx={{ 
                  p: { xs: 1, sm: 1.5 },
                  mr: { xs: 1, sm: 2 },
                  color: 'white',
                  '&:hover': { 
                    bgcolor: 'rgba(255,255,255,0.1)',
                    transform: 'scale(1.05)'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <MenuIcon sx={{ fontSize: { xs: '28px', sm: '32px' } }} />
              </IconButton>
              
              <Drawer
                anchor="left"
                open={open}
                onClose={toggleDrawer(false)}
                PaperProps={{
                  sx: {
                    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                    '& .MuiListItemIcon-root': {
                      fontSize: '1.25rem'
                    }
                  }
                }}
              >
                {DrawerContent}
              </Drawer>
            </Box>

            {/* Logo central */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                flexGrow: 1,
                gap: { xs: 1, sm: 2 }
              }}
            >
              <img 
                src={Logo} 
                alt="Logo" 
                style={{ 
                  height: '60px',
                  filter: 'brightness(0) invert(1)',
                  transition: 'transform 0.2s ease-in-out'
                }}
                onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
              />
            </Box>

            {/* Menú de usuario y notificaciones */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: { xs: 1, sm: 2 }
              }}
            >
              <NotificationRing />
              <InterfaceMenu employee={employee} />
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      <Box className="practice-banner">
        <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '16px' }}>
          Esta es la aplicación de solo prueba - Rama de Práctica
        </Typography>
      </Box>
    </>
  );
}

export default HeaderAuth;